import * as Sentry from '@sentry/nextjs';
import { useQuery, type UseQueryOptions, type UseQueryResult } from '@tanstack/react-query';
import { useMemo } from 'react';
import { mainnet } from 'viem/chains';
import { ZodError } from 'zod';

import { ApiError } from '@/lib/errors/api';

import { CHAIN_IDS } from '@/constants/chain';

import { useSwapFeeQuery } from '@/hooks/transactionModule/useSwapFeeQuery';

import { liquiditySourcesEventHandlers, useLiquiditySourcesStore } from '@/store/liquiditySources';
import {
  recipientAddressSelector,
  useMarketTradeSettingsStore,
} from '@/store/tradeSettings/market';

import { SwapPrice } from '@/utils/0x.types';
import { getTokenPair } from '@/utils/amplitude';
import { zeroExApiFetcher } from '@/utils/fetcher';
import { ChainAddress, isEthereumAddress } from '@/utils/models';
import { routes } from '@/utils/routes';
import { withSentSlippage } from '@/utils/slippage/sentSlippage';
import { getFeeRequestParams } from '@/utils/swap-fee-params';

import { useTokenResult } from './tokenQueries/useTokenResult';
import { useMatchaWallets } from './useMatchaWallets';
import { useResolvedSlippage } from './useResolvedSlippage';

export interface UseSwapPriceParams {
  /** Token address to buy */
  buyTokenAddress: ChainAddress;
  /** The chain Id of the token to buy */
  buyTokenChainId: number;
  /** Amount of sell token in wei */
  sellAmount: string;
  /** Token address to sell */
  sellTokenAddress: ChainAddress;
  /** The chain Id of the token to sell */
  sellTokenChainId: number;
  /** Routes the gasless/standard split: a standard price is not fetched while the trade is gasless. */
  isGasless: boolean;
  /** Buy-side amounts for the metrics/error context only; never used in the request. */
  telemetry: { buyAmountDecimal: string; buyAmountUsd: number };
}

export type SwapPriceQueryOptions = Omit<
  UseQueryOptions<SwapPrice, ApiError | ZodError | Error>,
  'queryKey' | 'queryFn'
>;

/**
 * Fetches the swap `/price` for a standard market order.
 */
export function useSwapPrice(
  params: UseSwapPriceParams | null,
  options: SwapPriceQueryOptions,
): UseQueryResult<SwapPrice, ApiError | ZodError | Error> {
  Sentry.setTag('app.0x_api_version', 2);
  const {
    sellTokenAddress,
    buyTokenAddress,
    sellAmount,
    sellTokenChainId,
    buyTokenChainId,
    isGasless = false,
    telemetry: { buyAmountDecimal = '', buyAmountUsd = 0 } = {},
  } = params ?? {};

  const { token: sellTokenResult } = useTokenResult(sellTokenAddress, sellTokenChainId);
  const { token: buyTokenResult } = useTokenResult(buyTokenAddress, buyTokenChainId);
  const sellTokenSymbol = sellTokenResult?.symbol;
  const buyTokenSymbol = buyTokenResult?.symbol;

  // get parameters
  const chainId = sellTokenChainId ?? buyTokenChainId ?? mainnet.id;
  const { ethereumWallet } = useMatchaWallets();

  const excludedSources = useLiquiditySourcesStore(
    liquiditySourcesEventHandlers,
  ).getExcludedSources(chainId);
  const {
    bps: resolvedSlippageBps,
    isReady: isSlippageReady,
    isAuto,
  } = useResolvedSlippage({
    chainId,
    sellToken: sellTokenResult,
    buyToken: buyTokenResult,
  });
  const { recipientAddress } = useMarketTradeSettingsStore(recipientAddressSelector);

  const { swapFeeBps, swapFeeRecipient, swapFeeToken } = useSwapFeeQuery({
    buyTokenAddress:
      buyTokenAddress && isEthereumAddress(buyTokenAddress) ? buyTokenAddress : undefined,
    buyTokenChainId,
    sellTokenAddress:
      sellTokenAddress && isEthereumAddress(sellTokenAddress) ? sellTokenAddress : undefined,
    sellTokenChainId,
  });

  // build URL
  const url = useMemo(() => {
    if (
      !sellTokenAddress ||
      !buyTokenAddress ||
      !sellAmount ||
      parseInt(sellAmount, 10) === 0 || // don't fetch a zero amount
      sellTokenChainId !== buyTokenChainId || // tokens can't be on different chains
      isGasless || // If gasless is set to true, we don't want to fetch a standard swap price
      sellTokenChainId === CHAIN_IDS.SOLANA ||
      buyTokenChainId === CHAIN_IDS.SOLANA
    )
      return null;

    const params = new URLSearchParams({
      chainId: chainId.toString(),
      buyToken: buyTokenAddress,
      sellToken: sellTokenAddress,
      sellAmount,
      slippageBps: resolvedSlippageBps.toString(),
      ...getFeeRequestParams({ swapFeeBps, swapFeeRecipient, swapFeeToken }),
    });
    if (ethereumWallet) params.set('taker', ethereumWallet.address);
    if (excludedSources) params.set('excludedSources', excludedSources);
    if (recipientAddress) params.set('recipient', recipientAddress);

    return routes.api.SWAP_PRICE + '?' + params.toString();
  }, [
    sellTokenAddress,
    buyTokenAddress,
    sellAmount,
    sellTokenChainId,
    buyTokenChainId,
    isGasless,
    chainId,
    resolvedSlippageBps,
    swapFeeBps,
    swapFeeRecipient,
    swapFeeToken,
    ethereumWallet,
    excludedSources,
    recipientAddress,
  ]);

  // build context for metrics & error reporting
  const context = useMemo(
    () => ({
      buy_token_address: buyTokenAddress,
      buy_token_amount: buyAmountDecimal,
      buy_token_symbol: buyTokenSymbol,
      chain_id: chainId,
      order_type: 'market_standard',
      pair: getTokenPair(
        sellTokenAddress && sellTokenSymbol
          ? {
              address: sellTokenAddress,
              chainId: chainId,
              symbol: sellTokenSymbol,
            }
          : undefined,
        buyTokenAddress && buyTokenSymbol
          ? {
              address: buyTokenAddress,
              chainId: chainId,
              symbol: buyTokenSymbol,
            }
          : undefined,
      ),
      sell_token_address: sellTokenAddress,
      sell_token_amount: sellAmount,
      sell_token_symbol: sellTokenSymbol,
      usd_dollar_amount: buyAmountUsd,
      wallet_connected_address: ethereumWallet?.address ?? null,
      swap_fee_bps: swapFeeBps,
      swap_fee_recipient: swapFeeRecipient,
    }),
    [
      buyAmountDecimal,
      buyAmountUsd,
      buyTokenAddress,
      buyTokenSymbol,
      chainId,
      sellAmount,
      sellTokenAddress,
      sellTokenSymbol,
      swapFeeBps,
      swapFeeRecipient,
      ethereumWallet,
    ],
  );

  const { enabled: callerEnabled = true, ...restOptions } = options;
  return useQuery<SwapPrice, ApiError | ZodError | Error>({
    queryKey: ['swap-price', url],
    queryFn: async () => withSentSlippage(await zeroExApiFetcher(url!, context), url!, isAuto),
    ...restOptions,
    // Computed last so it overrides any `enabled` in `restOptions`; we
    // AND-merge so the leaf still self-disables when the URL guard fails
    // even if the caller passes `enabled: true`. `isSlippageReady` holds the
    // price until Auto resolves, so it is fetched once at the final tolerance.
    enabled: url !== null && callerEnabled && isSlippageReady,
  });
}

/**
 * Ensures the conditions to request a price are satisfied.
 * @param sellTokenAdress - token to sell.
 * @param buyTokenAddress - token to purchase.
 * @param sellAmount - amount to sell.
 * @param buyAmount - amount to purchase.
 */
export const hasRequiredPriceParams = (
  sellTokenAddress: string | undefined,
  buyTokenAddress: string | undefined,
  sellAmount: string | undefined,
  buyAmount?: string,
): boolean => {
  if (!sellTokenAddress || !buyTokenAddress) {
    return false;
  }
  if (!!sellAmount) {
    const amount = Number(sellAmount);
    return !isNaN(amount) && amount > 0;
  }
  if (!!buyAmount) {
    const amount = Number(buyAmount);
    return !isNaN(amount) && amount > 0;
  }
  return false;
};
